/**
 * PATH: packages/email-hub/src/feature/components/JobPeekPanel.tsx
 * FILE_ID: EMAILHUB-JOB-PEEK-001
 * PURPOSE: Peek-first job panel for the email reader's job chip. Shows
 *          the job's number/name, client, site address, scope and status
 *          over the inbox; the footer "Open job" does the full page
 *          navigation the chip used to do on first click.
 * WHY:     Triage v5 (operator 2026-07-07): "clicking it opens the job
 *          ... takes us [there]" but mid-triage most clicks are
 *          verification - the peek answers those without losing the
 *          email context; full nav is one more click.
 * FLOW:
 *   ┌────────┐   ┌────────────────────────┐   ┌─────────────────┐
 *   │ jobId  │ → │ GET /jobs/{id} (query  │ → │ EntityPeekShell │
 *   │        │   │ key ['job', id] shared │   │ + Open job nav  │
 *   │        │   │ with invoice modal)    │   │                 │
 *   └────────┘   └────────────────────────┘   └─────────────────┘
 * DEPENDENCIES:
 *   - ../../_internal/hooks/useApi: same axios client LinkToJobModal uses
 *   - ./EntityPeekShell: the shared slide-over chrome
 * RELATED:
 *   - EmailPreviewPane.tsx / EmailReader.tsx (the job chips that open it)
 *   - tenant-shared CreateInvoiceModalRich (the ['job', id] cache twin)
 */

import { useQuery } from '@tanstack/react-query'
import { Briefcase, Loader2, MapPin, User } from 'lucide-react'

import api from '../../_internal/hooks/useApi'
import { EntityPeekShell } from './EntityPeekShell'

/** The job-detail fields this panel renders (subset of GET /jobs/{id}). */
export interface JobPeekDetail {
  id: number
  job_number?: string | null
  job_name?: string | null
  name?: string | null
  client_name?: string | null
  site_address?: string | null
  scope?: string | null
  status?: string | null
}

/** Pure I→O: job detail (partial) + fallback label → the peek heading. */
export function jobPeekTitle(
  detail: JobPeekDetail | undefined,
  fallbackName: string | null | undefined,
  jobId: number,
): string {
  const number = detail?.job_number ? `${detail.job_number}` : ''
  const name = detail?.job_name || detail?.name || fallbackName || ''
  if (number && name && name !== number) return `${number} · ${name}`
  return number || name || `Job #${jobId}`
}

export interface JobPeekPanelProps {
  jobId: number
  /** List-sourced label so the header paints before the fetch lands. */
  jobName?: string | null
  onClose: () => void
  /** Full navigation to /jobs/{id} (host owns the router). */
  onOpen: () => void
}

export function JobPeekPanel({ jobId, jobName, onClose, onOpen }: JobPeekPanelProps) {
  // Same key as tenant-shared's invoice modal so the two share one cache.
  const jobQ = useQuery<JobPeekDetail>({
    queryKey: ['job', jobId],
    queryFn: () => api.get(`/jobs/${jobId}`).then((r) => r.data),
    staleTime: 60_000,
  })
  const detail = jobQ.data

  const row = (icon: React.ReactNode, label: string, value: string | null | undefined) =>
    value ? (
      <div className="flex items-start gap-2 text-sm">
        <span className="mt-0.5 text-muted-foreground shrink-0">{icon}</span>
        <div className="min-w-0">
          <div className="text-[10.5px] uppercase tracking-wide text-muted-foreground/70">
            {label}
          </div>
          <div className="text-foreground break-words">{value}</div>
        </div>
      </div>
    ) : null

  return (
    <EntityPeekShell
      title={jobPeekTitle(detail, jobName, jobId)}
      subtitle="Job"
      icon={<Briefcase className="h-4 w-4 text-blue-500" aria-hidden />}
      onClose={onClose}
      onOpen={onOpen}
      openLabel="Open job"
    >
      {jobQ.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Loading job…
        </div>
      ) : jobQ.isError ? (
        <div className="text-sm text-muted-foreground py-4">
          Could not load the job summary. "Open job" still works.
        </div>
      ) : (
        <div className="space-y-3">
          {detail?.status && (
            <span className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
              {detail.status}
            </span>
          )}
          {row(<User className="h-3.5 w-3.5" aria-hidden />, 'Client', detail?.client_name)}
          {row(<MapPin className="h-3.5 w-3.5" aria-hidden />, 'Site', detail?.site_address)}
          {detail?.scope && (
            <div className="text-xs text-muted-foreground leading-relaxed">{detail.scope}</div>
          )}
        </div>
      )}
    </EntityPeekShell>
  )
}

export default JobPeekPanel
